/**
 * BTW. Menuprijzen zijn consumentenprijzen: het bedrag op de tegel is
 * inclusief BTW. Uit dat brutobedrag halen we het BTW-deel.
 *
 * Regel 10: statiegeld is geen omzet en valt buiten de BTW. Daarvoor is het
 * tarief GEEN_BTW; die regels tellen niet mee in de omzet per tarief.
 */

import { afrondCenten, eisCent, type Cent } from './geld.js';

/** BTW-tarief in basispunten: 9% = 900, 21% = 2100. */
export const BTW_GEEN = 0 as const;
export const BTW_LAAG = 900 as const;
export const BTW_HOOG = 2100 as const;

export type BtwTarief = typeof BTW_GEEN | typeof BTW_LAAG | typeof BTW_HOOG;

export const BTW_TARIEVEN: readonly BtwTarief[] = [BTW_GEEN, BTW_LAAG, BTW_HOOG];

export function isBtwTarief(waarde: unknown): waarde is BtwTarief {
  return waarde === BTW_GEEN || waarde === BTW_LAAG || waarde === BTW_HOOG;
}

export function eisBtwTarief(waarde: unknown): BtwTarief {
  if (!isBtwTarief(waarde)) {
    throw new Error(`onbekend BTW-tarief: ${String(waarde)} (verwacht 0, 900 of 2100)`);
  }
  return waarde;
}

/** Toont een tarief als "9%" of "21%", "geen" voor statiegeld. */
export function toonBtwTarief(tarief: BtwTarief): string {
  if (tarief === BTW_GEEN) return 'geen';
  return `${tarief / 100}%`;
}

export interface BtwSplitsing {
  /** Wat de klant betaalt. */
  brutoCent: Cent;
  /** Bruto minus BTW. */
  nettoCent: Cent;
  btwCent: Cent;
  tarief: BtwTarief;
}

/**
 * Haalt de BTW uit een brutobedrag.
 * btw = bruto * tarief / (10000 + tarief), afgerond op hele centen.
 */
export function splitsBtwUitBruto(brutoCent: Cent, tarief: BtwTarief): BtwSplitsing {
  eisCent(brutoCent, 'brutobedrag');
  eisBtwTarief(tarief);

  if (tarief === BTW_GEEN) {
    return { brutoCent, nettoCent: brutoCent, btwCent: 0, tarief };
  }

  const btwCent = afrondCenten((brutoCent * tarief) / (10_000 + tarief));
  return { brutoCent, nettoCent: brutoCent - btwCent, btwCent, tarief };
}

/** Rekent andersom: van een nettobedrag naar bruto. Voor onze eigen facturen. */
export function telBtwOpBijNetto(nettoCent: Cent, tarief: BtwTarief): BtwSplitsing {
  eisCent(nettoCent, 'nettobedrag');
  eisBtwTarief(tarief);

  if (tarief === BTW_GEEN) {
    return { brutoCent: nettoCent, nettoCent, btwCent: 0, tarief };
  }

  const btwCent = afrondCenten((nettoCent * tarief) / 10_000);
  return { brutoCent: nettoCent + btwCent, nettoCent, btwCent, tarief };
}

export interface BtwRegel {
  brutoCent: Cent;
  tarief: BtwTarief;
}

export interface BtwOverzicht {
  perTarief: BtwSplitsing[];
  totaalBrutoCent: Cent;
  totaalNettoCent: Cent;
  totaalBtwCent: Cent;
  /** Bedragen zonder BTW-plicht, zoals statiegeld. Zit niet in de omzet. */
  buitenBtwCent: Cent;
}

/**
 * Telt regels op per tarief en splitst daarna pas de BTW af.
 * Eerst optellen, dan afronden: dat voorkomt dat de afrondingsfout per regel
 * meeloopt en het BTW-totaal op een bon van honderd regels een cent afwijkt
 * van hetzelfde totaal op de dagstaat.
 */
export function btwOverzicht(regels: readonly BtwRegel[]): BtwOverzicht {
  const brutoPerTarief = new Map<BtwTarief, Cent>();
  for (const regel of regels) {
    const tarief = eisBtwTarief(regel.tarief);
    eisCent(regel.brutoCent, 'brutobedrag');
    brutoPerTarief.set(tarief, (brutoPerTarief.get(tarief) ?? 0) + regel.brutoCent);
  }

  const perTarief: BtwSplitsing[] = [];
  let totaalBrutoCent = 0;
  let totaalNettoCent = 0;
  let totaalBtwCent = 0;
  let buitenBtwCent = 0;

  for (const tarief of BTW_TARIEVEN) {
    const bruto = brutoPerTarief.get(tarief);
    if (bruto === undefined) continue;
    const splitsing = splitsBtwUitBruto(bruto, tarief);
    perTarief.push(splitsing);
    if (tarief === BTW_GEEN) {
      buitenBtwCent += splitsing.brutoCent;
      continue;
    }
    totaalBrutoCent += splitsing.brutoCent;
    totaalNettoCent += splitsing.nettoCent;
    totaalBtwCent += splitsing.btwCent;
  }

  return { perTarief, totaalBrutoCent, totaalNettoCent, totaalBtwCent, buitenBtwCent };
}

/** Wat de klant in totaal betaalt, inclusief statiegeld. */
export function teBetalenTotaal(regels: readonly BtwRegel[]): Cent {
  const overzicht = btwOverzicht(regels);
  return overzicht.totaalBrutoCent + overzicht.buitenBtwCent;
}
